import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Terms + Privacy modal shown from the registration wizard. Both
 * documents render off i18n keys in the `legal` namespace.
 */
export function LegalModal({
  initialTab,
  onClose,
}: {
  initialTab: 'terms' | 'privacy';
  onClose: () => void;
}) {
  const { t } = useTranslation('legal');
  const [tab, setTab] = useState<'terms' | 'privacy'>(initialTab);

  return (
    <div className="legal-modal-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="legal-modal" role="dialog" aria-modal="true">
        <header className="legal-modal-hdr">
          <div className="legal-modal-title">
            {tab === 'terms' ? t('modal.title_terms') : t('modal.title_privacy')}
          </div>
          <button className="legal-modal-close" onClick={onClose} aria-label={t('modal.close')}>
            ✕
          </button>
        </header>
        <div className="legal-modal-tabs">
          <button
            className={`legal-mt ${tab === 'terms' ? 'active' : ''}`}
            onClick={() => setTab('terms')}
          >
            {t('modal.tab_terms')}
          </button>
          <button
            className={`legal-mt ${tab === 'privacy' ? 'active' : ''}`}
            onClick={() => setTab('privacy')}
          >
            {t('modal.tab_privacy')}
          </button>
        </div>
        <div className="legal-modal-body">
          {tab === 'terms' ? <TermsBody /> : <PrivacyBody />}
        </div>
        <footer className="legal-modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            {t('modal.close')}
          </button>
          <button className="btn-primary" onClick={onClose}>
            {t('modal.agree')}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Section({ children }: { children: ReactNode }) {
  return <div className="legal-section">{children}</div>;
}

function H2({ children }: { children: ReactNode }) {
  return <h3 className="legal-h2">{children}</h3>;
}

function P({ children }: { children: ReactNode }) {
  return <p className="legal-p">{children}</p>;
}

function LI({ children }: { children: ReactNode }) {
  return <div className="legal-li">{children}</div>;
}

export function TermsBody() {
  const { t } = useTranslation('legal');
  return (
    <Section>
      <h2 className="legal-h1">{t('terms.h1')}</h2>
      <span className="legal-updated">{t('modal.updated')}</span>
      <P>{t('terms.p_intro')}</P>
      <H2>{t('terms.h_definitions')}</H2>
      <P>{t('terms.p_definitions')}</P>
      <H2>{t('terms.h_license')}</H2>
      <P>{t('terms.p_license')}</P>
      <H2>{t('terms.h_account')}</H2>
      <LI>{t('terms.li_account_1')}</LI>
      <LI>{t('terms.li_account_2')}</LI>
      <LI>{t('terms.li_account_3')}</LI>
      <H2>{t('terms.h_billing')}</H2>
      <P>{t('terms.p_billing')}</P>
      <H2>{t('terms.h_data')}</H2>
      <P>{t('terms.p_data')}</P>
      <H2>{t('terms.h_liability')}</H2>
      <P>{t('terms.p_liability')}</P>
      <H2>{t('terms.h_changes')}</H2>
      <P>{t('terms.p_changes')}</P>
      <H2>{t('terms.h_law')}</H2>
      <P>{t('terms.p_law')}</P>
    </Section>
  );
}

export function PrivacyBody() {
  const { t } = useTranslation('legal');
  return (
    <Section>
      <h2 className="legal-h1">{t('privacy.h1')}</h2>
      <span className="legal-updated">{t('modal.updated')}</span>
      <P>{t('privacy.p_intro')}</P>
      <H2>{t('privacy.h_collected')}</H2>
      <LI>{t('privacy.li_collected_1')}</LI>
      <LI>{t('privacy.li_collected_2')}</LI>
      <LI>{t('privacy.li_collected_3')}</LI>
      <LI>{t('privacy.li_collected_4')}</LI>
      <H2>{t('privacy.h_usage')}</H2>
      <LI>{t('privacy.li_usage_1')}</LI>
      <LI>{t('privacy.li_usage_2')}</LI>
      <LI>{t('privacy.li_usage_3')}</LI>
      <LI>{t('privacy.li_usage_4')}</LI>
      <H2>{t('privacy.h_security')}</H2>
      <P>{t('privacy.p_security')}</P>
      <H2>{t('privacy.h_third_party')}</H2>
      <P>{t('privacy.p_third_party')}</P>
      <H2>{t('privacy.h_rights')}</H2>
      <LI>{t('privacy.li_rights_1')}</LI>
      <LI>{t('privacy.li_rights_2')}</LI>
      <LI>{t('privacy.li_rights_3')}</LI>
      <LI>{t('privacy.li_rights_4')}</LI>
      <H2>{t('privacy.h_cookies')}</H2>
      <P>{t('privacy.p_cookies')}</P>
      <H2>{t('privacy.h_contact')}</H2>
      <P>{t('privacy.p_contact')}</P>
    </Section>
  );
}
